// Pure payload-building + validation for the consult RFZO request
// (POST /rfzo_therapy). Extracted from React so the logic is unit-testable
// without rendering. Consumed by useConsultOrderLifecycle (submitRfzo) and by
// ConsultRfzoFormModal (field-linked errors).
//
// ITAM-2887: the advisor raises an EMPTY request; the pharmacy is the
// per-draft pick, and the caller's JMBG consent checkbox is mandatory
// (UI-only, never sent).

import { ConsultRfzoDraft } from "../contexts/ConsultOrderContext";
import { ConsultRfzoRequestBody } from "../types/consultRfzo.types";
import { ConsultPharmacyPair } from "../types/consultOrder.types";

export interface ConsultRfzoValidationErrors {
  pharmacy?: string;
  lbo?: string;
  jmbg?: string;
  firstName?: string;
  secondName?: string;
  consent?: string;
}

export interface ConsultRfzoValidationResult {
  valid: boolean;
  errors: ConsultRfzoValidationErrors;
}

export const RFZO_PHARMACY_REQUIRED_ERROR = "Izaberite apoteku.";
export const RFZO_PHARMACY_NO_RFZO_ERROR =
  "Izabrana apoteka ne obrađuje RFZO terapije. Izaberite drugu apoteku.";
export const RFZO_PHARMACY_UNKNOWN_NOTICE =
  "Nije moguće proveriti da li apoteka obrađuje RFZO terapije.";
export const RFZO_CONSENT_REQUIRED_ERROR =
  "Potvrdite saglasnost korisnika za prosleđivanje JMBG-a.";

/**
 * Validate the RFZO request draft for submit-time gating.
 *
 * The identifier rules mirror the server exactly, trim-before-test included.
 * This is the PRIMARY guard, not a convenience: the server answers an
 * identifier rejection with a bare Serbian sentence carrying no errorCode and
 * no field key, so nothing that reaches the server can be mapped back to a
 * field.
 */
export const validateConsultRfzoDraft = (
  draft: ConsultRfzoDraft,
): ConsultRfzoValidationResult => {
  const errors: ConsultRfzoValidationErrors = {};

  if (draft.pharmacy == null) {
    errors.pharmacy = RFZO_PHARMACY_REQUIRED_ERROR;
  } else if (draft.pharmacy.haveRFZO === 0) {
    // A confirmed non-RFZO pharmacy blocks (Q16). Unknown (null) does not: the
    // backend decides.
    errors.pharmacy = RFZO_PHARMACY_NO_RFZO_ERROR;
  }

  const lbo = draft.lbo.trim();
  const jmbg = draft.jmbg.trim();
  if (!lbo && !jmbg) {
    const message = "Unesite LBO ili JMBG korisnika.";
    errors.lbo = message;
    errors.jmbg = message;
  } else if (jmbg && !/^[0-9]{13}$/.test(jmbg)) {
    // Digit count and charset only: the server runs no checksum and no date
    // plausibility check, so a stricter rule here would reject records the
    // backend itself accepts.
    errors.jmbg = "JMBG mora imati tačno 13 cifara.";
  }
  // The patient name on the request; without it the backend stores the
  // placeholder "RFZO porudžbina".
  if (!draft.firstName.trim()) {
    errors.firstName = "Ime je obavezno.";
  }
  if (!draft.secondName.trim()) {
    errors.secondName = "Prezime je obavezno.";
  }

  if (!draft.consent) {
    errors.consent = RFZO_CONSENT_REQUIRED_ERROR;
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

/**
 * Build the exact POST /rfzo_therapy body. The identifier keys are `userLBO`
 * and `userJmbg`, the note travels as `note`, and the client is identified by
 * `userId` (the caller's id from the consult prefill, never the advisor's).
 * Values are trimmed and empty optional strings are omitted. The consent flag
 * is never sent.
 */
export const buildConsultRfzoRequestBody = (
  draft: ConsultRfzoDraft,
  userId: number,
  pharmacyPair: ConsultPharmacyPair,
): ConsultRfzoRequestBody => {
  const body: ConsultRfzoRequestBody = {
    userId,
    marketplaceId: pharmacyPair.marketplaceId,
    branchMarketplaceId: pharmacyPair.branchMarketplaceId,
  };

  // The server normalizes a blank identifier to null itself, so an empty
  // string would be harmless. Omitting keeps the body honest about what the
  // advisor actually entered.
  const lbo = draft.lbo.trim();
  if (lbo) {
    body.userLBO = lbo;
  }
  const jmbg = draft.jmbg.trim();
  if (jmbg) {
    body.userJmbg = jmbg;
  }

  const firstName = draft.firstName.trim();
  if (firstName) {
    body.firstName = firstName;
  }
  const secondName = draft.secondName.trim();
  if (secondName) {
    body.secondName = secondName;
  }
  const userPhone = draft.userPhone.trim();
  if (userPhone) {
    body.userPhone = userPhone;
  }
  const note = draft.note.trim();
  if (note) {
    body.note = note;
  }

  return body;
};
